/**
 * Signing deadlines — the frontend half of `backend/app/services/deadlines.py`.
 *
 * A picked deadline is posted as the bare date ("YYYY-MM-DD"). The backend reads
 * it as 11:59 PM on that date in the sender's time zone, so the app and the API
 * cannot disagree. Never `new Date("YYYY-MM-DD").toISOString()`: a date-only
 * string parses as UTC MIDNIGHT — 8 PM the evening before in New York.
 */

function pad(n: number) {
  return n < 10 ? `0${n}` : `${n}`;
}

/** The viewer's LOCAL calendar date `days` from today, as "YYYY-MM-DD".
 *  (`toISOString().split("T")[0]` is the UTC date — a day ahead every evening
 *  in the Americas.) */
export function localYMD(days = 0, now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Shown under every deadline picker — names the zone the server will use
 *  (`useAccountTimeZone`), never "your time". */
export function deadlineHint(zone: string | null): string {
  return zone
    ? `Signing closes at 11:59 PM on that day (${zone.replace(/_/g, " ")}). Change your time zone in Settings.`
    : "Signing closes at 11:59 PM on that day.";
}

/** The browser's IANA zone, or null. */
export function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** "Oct 7, 11:59 PM EDT" — an instant in the viewer's own zone, named. */
export function formatClosing(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    timeZoneName: "short", ...(timeZone ? { timeZone } : {}),
  });
}

/** "Closes in 5 h" / "in 40 min" / "in 3 days" — a countdown needs no zone.
 *  Days round DOWN past the first day ("in 1 day" with 47 h left), hours on the last
 *  day, so it never promises more time than there is. Null once it has passed. */
export function closesIn(iso: string, now: number = Date.now()): string | null {
  const ms = new Date(iso).getTime() - now;
  if (!(ms > 0)) return null;
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `in ${Math.max(min, 1)} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `in ${h} h`;
  const d = Math.floor(h / 24);
  return `in ${d} day${d === 1 ? "" : "s"}`;
}
